/**
 * 한국어 "내 주변(near me)" 카피 — 제2파: 주요 9개 클러스터 이외의 전 서비스.
 *
 * 수작업 번역(빌드/런타임 모두 AI 크레딧 소모 없음). 사실관계는
 * ./registry.ts / ./en-copy-2.ts / ./ja-copy-2.ts 와 일치시켰습니다.
 *  - 태국은 헤이그 아포스티유 협약 가입국이 아닙니다. 해외에서 사용할 태국
 *    공문서는 외교부 영사국(Legalization Division) 인증을 받고, 필요 시 주태국
 *    해당국 대사관 인증을 추가합니다.
 *  - 가격은 게시하지 않고 건별 견적입니다. 처리 기간·수리 결과는 보장하지
 *    않으며, 수리 여부는 제출 기관이 결정합니다.
 *  - 저희는 "대행만 하는 곳"이 아니라 순서와 증빙을 먼저 정리해 드리는
 *    자문형 팀입니다(국경 간 서류 실무 15년 이상).
 */

import type { NearServiceKey } from "./registry";
import type { NearKoCopy } from "./ko-copy";

export const NEAR_KO2_KEYS = [
  "marriage",
  "longstay",
  "corporate",
  "immigration",
  "estate",
  "civilreg",
  "family",
  "accounting",
  "interpreter",
  "embassy",
  "ip",
  "litigation",
  "pdpa",
  "misc-official",
  "council",
  "due-diligence",
  "pension",
  "work-permit",
  "expert",
] as const satisfies readonly NearServiceKey[];

export type NearKo2Key = (typeof NEAR_KO2_KEYS)[number];

const MFA = "제출 기관이 요구하는 경우 외교부 영사국 인증을 받고, 이어서 주태국 해당국 대사관 인증을 추가합니다.";

export const NEAR_KO2_COPY: Record<NearKo2Key, NearKoCopy> = {
  marriage: {
    label: "태국 국제결혼 서류 서비스",
    definition:
      "태국에서의 국제결혼은 한 번의 방문으로 끝나는 절차가 아니라 서류의 연쇄입니다. 주태국 자국 대사관에서 혼인요건구비증명(독신증명)을 받고, 인증 번역으로 태국어본을 만들고, 외교부 영사국 인증을 거친 뒤 구청(암퍼)에서 혼인등록을 합니다.",
    chain: [
      "소속 대사관의 현행 혼인요건구비증명·독신증명 절차와 예약 규칙을 확인합니다.",
      "대사관 증명서를 받은 뒤 인증 번역자가 태국어로 번역합니다.",
      "번역본을 외교부 영사국에서 인증합니다.",
      "당사자 두 사람이 구청에 출석해 혼인등록을 합니다.",
      "해외 사용을 위해 혼인증명서 등본·번역·인증을 준비합니다.",
    ],
    checklist: [
      "외국인 배우자의 여권과 입국 스탬프 페이지.",
      "태국인 배우자의 신분증과 주민등록부(타비안반).",
      "대사관 발급 혼인요건구비증명 또는 독신증명.",
      "혼인 이력이 있으면 이혼증명 또는 배우자 사망증명.",
      "구청 등록 시 필요한 증인 2인.",
    ],
    pitfalls: [
      "외교부 인증이 끝나기 전에 구청 예약을 잡는 경우.",
      "대사관 증명서의 이름 표기가 여권과 일치하지 않는 경우.",
      "이전 혼인의 종료를 증명하는 서류가 빠진 경우.",
      "태국 혼인증명서를 그대로 해외에서 쓸 수 있다고 오해하는 경우.",
    ],
    remote: "번역·인증·공정 관리는 원격으로 진행 가능합니다. 대사관 예약과 혼인등록 자체는 당사자 출석이 필요합니다.",
  },
  longstay: {
    label: "장기체류·은퇴비자 연장 서류 서비스",
    definition:
      "장기체류 연장은 제출된 증빙을 근거로 이민국이 판단합니다. 저희는 서류 준비와 자문을 맡아 증빙을 항목별로 정리하고 수리되기 쉬운 형식으로 구성합니다.",
    chain: [
      "정확한 연장 카테고리와 현행 증빙 목록을 확인합니다.",
      "보유 서류와 대조해 부족한 부분을 도출합니다.",
      "재정·주소·보험 증빙을 수리되는 형식으로 준비합니다.",
      "제출 순서대로 정리하고 체크리스트를 첨부합니다.",
      "제출 시 동행·지원하고 추가 자료 요구에 대응합니다.",
    ],
    checklist: [
      "여권, 현재 비자, TM.6 또는 출입국 기록.",
      "TM.30 주소 신고와 임대차계약 또는 소유 증명.",
      "형식과 예치 기간 요건을 충족하는 은행 서류.",
      "카테고리가 요구하는 건강보험 서류.",
      "가족 카테고리는 혼인·가족관계 서류.",
    ],
    pitfalls: [
      "은행 잔고증명이 요구되는 예치 기간을 채우지 못한 경우.",
      "TM.30 신고 누락 — 연장 심사에서 가장 먼저 확인합니다.",
      "체류 만료 직전에야 제출하는 경우.",
      "연장이 보장된다고 생각하는 경우 — 판단은 심사관이 합니다.",
    ],
    remote: "서류 준비와 점검은 원격으로 끝낼 수 있습니다. 이민국 방문은 본인 출석이 필요합니다.",
  },
  corporate: {
    label: "법인 서류·회사 인증 서비스",
    definition:
      "법인 서류 업무는 법인등기부(회사증명서), DBD 등본, 이사회 결의서, 위임장과 이들의 해외 사용을 위한 인증을 포함합니다. 계좌 개설, 입찰, 대리인 수권, 해외 자회사 설립 등에 사용됩니다.",
    chain: [
      "제출처가 요구하는 정확한 서류 목록과 발급 후 유효기간을 확인합니다.",
      "최신 DBD 등본과 회사증명서를 발급받습니다.",
      "상대방이 수용하는 문구로 결의서·위임장을 작성합니다.",
      "공증업무 변호사가 서명과 사본을 인증합니다.",
      MFA,
    ],
    checklist: [
      "회사증명서와 DBD 등기 등본(최근 발급본).",
      "주주명부와 사업목적 등본.",
      "수권 이사의 여권 또는 태국 신분증.",
      "결의서·계약서 최종안.",
      "제출처의 서면 요건.",
    ],
    pitfalls: [
      "등본 발급일이 상대방의 유효기간을 초과한 경우.",
      "인증 예약 전에 미리 서명해 버리는 경우.",
      "회사증명서상 권한이 없는 이사가 결의서에 서명하는 경우.",
      "태국어 원본의 직인·양각 문자가 번역에서 누락되는 경우.",
    ],
    remote: "서명 입회를 제외한 모든 과정은 원격 처리가 가능합니다.",
  },
  immigration: {
    label: "이민국 서류 준비·자문 서비스",
    definition:
      "연장, 재입국 허가, 체류자격 변경, 각종 신고 등 이민 관련 신청은 각각 증빙 목록이 다릅니다. 저희는 준비와 순서 배열을 담당하고, 최종 판단은 태국 이민국이 합니다.",
    chain: [
      "신청 종류, 관할 사무소, 증빙 목록을 확인합니다.",
      "현재 체류 만료일과 과거 신청 내역의 정합성을 점검합니다.",
      "보완 서류를 준비하고 필요한 인증을 진행합니다.",
      "제출 순서대로 정리하고 창구용 체크리스트를 첨부합니다.",
      "진행 상황을 추적하고 추가 자료 요구에 대응합니다.",
    ],
    checklist: [
      "여권과 모든 비자·스탬프 페이지.",
      "TM.30 주소 신고와 거주 증명.",
      "카테고리에 따른 취업·학업·가족관계 서류.",
      "현행 규격에 맞는 사진.",
      "대리 제출 시 위임장.",
    ],
    pitfalls: [
      "관할이 아닌 사무소에 제출하는 경우.",
      "TM.30·임대차계약·신청서의 주소가 서로 다른 경우.",
      "증빙을 모으는 사이 체류 기간을 넘기는 경우.",
      "사무소의 현행 목록이 아니라 오래된 인터넷 정보를 따르는 경우.",
    ],
    remote: "준비와 자문은 원격으로 가능합니다. 창구 방문은 본인 출석이 필요합니다.",
  },
  estate: {
    label: "유언·상속·유산 서류 서비스",
    definition:
      "태국의 상속은 법원 절차와 등기 절차의 두 갈래입니다. 유언(또는 법정상속)에서 시작해 법원이 상속재산관리인을 선임하고, 자산별 등기 기관에서 명의를 이전합니다. 국제 상속에서는 번역과 인증도 필요합니다.",
    chain: [
      "자산, 소재지, 상속인, 유언 유무 등 상속 전체 그림을 정리합니다.",
      "자산별로 법원 선임 관리인이 필요한지 확인합니다.",
      "신분·상속관계 증명을 갖추고 외국 서류에는 인증 번역을 붙입니다.",
      "변호사가 관리인 선임을 신청하고 기일에 출석합니다.",
      "법원 명령에 따라 각 등기 기관에서 자산 명의를 이전합니다.",
    ],
    checklist: [
      "사망증명서(외국 발급이면 인증 번역 포함).",
      "유언장(있는 경우)과 원본 보관 장소.",
      "상속관계 증명: 주민등록부, 출생·혼인 증명.",
      "자산 증명: 토지권리증, 은행 거래내역, 주주명부.",
      "상속인 전원의 여권 또는 신분증.",
    ],
    pitfalls: [
      "관리인 선임 명령 전에 은행·토지 명의 변경을 시도하는 경우.",
      "외국 서류에 인증된 태국어 번역이 없는 경우.",
      "상속인 누락으로 분할이 무효가 될 위험.",
      "외국 유언만으로 태국 토지를 처분할 수 있다고 오해하는 경우.",
    ],
    remote: "서류 정리와 초안 작성은 원격으로 가능합니다. 기일에는 변호사가 출석하며, 법원이 요구할 때만 본인 출석이 필요합니다.",
  },
  civilreg: {
    label: "주민·신분등록 서류 서비스",
    definition:
      "신분등록은 구청이 보관하는 출생·혼인·이혼·사망·개명·주민등록 기록을 다룹니다. 정정이나 등본 발급은 각각 증빙 목록이 다르고, 해외 사용에는 번역과 인증이 필요합니다.",
    chain: [
      "기록의 종류와 해당 기록을 보관하는 구청을 특정합니다.",
      "인증 등본을 신청하거나 정정을 신청합니다.",
      "해당 사무소가 지정하는 보완 증빙을 갖춥니다.",
      "해외 사용을 위한 인증 번역을 작성합니다.",
      MFA,
    ],
    checklist: [
      "태국 신분증과 주민등록부(외국인은 여권).",
      "이미 보유한 증명서나 등본.",
      "정정 내용을 뒷받침하는 증빙(학교·병원·법원 서류).",
      "저희가 대리 신청할 경우의 위임장.",
    ],
    pitfalls: [
      "원기록을 보관하지 않는 사무소에 신청하는 경우.",
      "정정 신청에 올바른 데이터를 보여줄 서류가 없는 경우.",
      "먼저 번역했는데 등본을 다시 발급받게 되어 작업이 낭비되는 경우.",
      "같은 가족 서류인데 이름의 로마자 표기가 통일되지 않은 경우.",
    ],
    remote: "신청과 진행 추적은 저희가 대행할 수 있습니다. 일부 정정은 기록 본인의 출석이 필요합니다.",
  },
  family: {
    label: "가족법 서류 서비스",
    definition:
      "이혼, 친권, 인지, 후견인 선임, 혼전계약 등 가족 사건은 내용에 따라 구청(합의 방식) 또는 법원에서 처리합니다. 처음에 경로를 정확히 고르면 지연의 상당 부분을 피할 수 있습니다.",
    chain: [
      "구청 합의 방식으로 충분한지, 법원이 필요한지 평가합니다.",
      "집행 가능한 문구로 합의서 또는 소장을 작성합니다.",
      "신분·혼인상태 증명을 갖추고 외국 서류에는 인증 번역을 붙입니다.",
      "구청에 등록하거나 법원에 제출합니다.",
      "해외 사용을 위한 인증 등본·번역·인증을 받습니다.",
    ],
    checklist: [
      "혼인증명서, 필요 시 혼전계약 서류.",
      "자녀의 출생증명서.",
      "쌍방의 태국 신분증, 주민등록부, 여권.",
      "친권·양육비 합의에 관한 증빙.",
      "외국 법원의 명령과 그 인증 번역.",
    ],
    pitfalls: [
      "합의 이혼 등록 시 재산과 친권을 서면으로 정하지 않는 경우.",
      "승인 절차 없이 외국 명령을 태국에서 바로 쓰려는 경우.",
      "문구 때문에 구청이나 은행이 나중에 이행을 거부하는 경우.",
      "태국과 외국의 기록이 자동으로 동기화된다고 오해하는 경우(동기화되지 않습니다).",
    ],
    remote: "초안 작성과 증빙 점검은 원격으로 가능합니다. 등록과 기일은 출석이 필요합니다.",
  },
  accounting: {
    label: "회계·세무·감사 서류 서비스",
    definition:
      "회계 서류 업무는 장부 기장, 재무제표, 감사 파일, 세무 신고, 그리고 은행·입찰 위원회·해외 모회사가 요구하는 경우의 인증과 번역을 포함합니다.",
    chain: [
      "제출처가 필요로 하는 재무제표와 대상 기간을 확인합니다.",
      "장부를 대조하고 재무제표 일체를 작성합니다.",
      "감사받은 재무제표가 필요하면 감사인이 서명합니다.",
      "재무제표와 감사 주석의 인증 번역을 작성합니다.",
      "공증업무 변호사가 인증하고, 해외 사용 시 외교부 인증을 받습니다.",
    ],
    checklist: [
      "회사증명서와 세무등록 서류.",
      "대상 기간의 시산표, 총계정원장, 은행 거래내역.",
      "전기 제출 재무제표와 납세 신고서.",
      "제출처의 서식 요건.",
    ],
    pitfalls: [
      "감사받은 재무제표를 요구받았는데 관리회계 자료를 제출하는 경우.",
      "감사인 주석의 원문 표현이 번역에서 사라지는 경우.",
      "나중에 수정될 중간본을 먼저 인증해 버리는 경우.",
      "회계연도와 제출처가 요구하는 기간이 어긋나는 경우.",
    ],
    remote: "자료 수집과 작성은 원격으로 진행합니다. 인증 단계에서만 원본이 필요합니다.",
  },
  interpreter: {
    label: "법원·관공서 통역 서비스",
    definition:
      "법원 기일, 경찰 조서, 공증 서명, 구청·이민국 방문에는 통역이 필요할 수 있습니다. 통역은 번역과 다른 업무이며, 기관에 따라 요구하는 자격과 형식이 다릅니다.",
    chain: [
      "기관 종류와 통역에 요구되는 자격·형식을 확인합니다.",
      "사건 배경과 용어집을 사전에 공유합니다.",
      "일정과 예상 소요 시간을 확정합니다.",
      "현장에서 통역하고 필요한 확인 서명을 진행합니다.",
      "필요하면 통역 확인서 또는 관련 서류 번역을 함께 준비합니다.",
    ],
    checklist: [
      "기일·예약 일시와 장소, 담당 부서.",
      "사건 개요와 관련 서류 사본.",
      "고유명사(이름·회사명)의 로마자·태국어 표기.",
      "기관이 요구하는 통역인 자격 조건.",
    ],
    pitfalls: [
      "기관이 요구하는 자격을 확인하지 않고 통역인을 섭외하는 경우.",
      "당일에야 서류를 전달해 용어 준비가 안 되는 경우.",
      "통역만으로 서면 번역까지 갈음된다고 오해하는 경우.",
      "기일 변경을 통보하지 않아 이중 일정이 발생하는 경우.",
    ],
    remote: "사전 준비와 용어 정리는 원격으로 합니다. 통역 자체는 현장 또는 기관이 허용하는 화상 방식으로 진행합니다.",
  },
  embassy: {
    label: "대사관·영사 서류 서비스",
    definition:
      "대사관·영사 업무는 각국 공관이 정한 자체 규칙을 따릅니다. 인증 대상, 예약 방법, 요구 번역 형식이 나라마다 다르므로 순서를 먼저 확인하는 것이 핵심입니다.",
    chain: [
      "해당 공관의 현행 요건과 예약 규칙을 확인합니다.",
      "태국 측 선행 절차(번역·외교부 인증)를 먼저 완료합니다.",
      "공관 양식과 신청서를 작성합니다.",
      "예약일에 접수하고 수령 일정을 관리합니다.",
      "완료된 서류를 국내외로 발송합니다.",
    ],
    checklist: [
      "여권과 신분 서류.",
      "인증 대상 원본과 사본.",
      "공관이 요구하는 번역본.",
      "대리 접수 시 위임장(공관이 허용하는 경우).",
    ],
    pitfalls: [
      "외교부 인증 전에 대사관 예약을 잡는 경우.",
      "공관마다 다른 번역 형식 요건을 일괄 적용하는 경우.",
      "예약 슬롯이 몇 주 뒤에만 열리는 점을 일정에 넣지 않은 경우.",
      "본인 출석이 필수인 공관에서 대리 접수를 시도하는 경우.",
    ],
    remote: "요건 확인·번역·선행 인증은 원격으로 처리합니다. 공관 접수는 규칙에 따라 본인 출석이 필요할 수 있습니다.",
  },
  ip: {
    label: "상표·저작권 등 지식재산 서류 서비스",
    definition:
      "태국의 상표·저작권·특허 업무는 상무부 지식재산국(DIP)에서 처리합니다. 외국 출원인은 태국 내 대리인 위임장과 인증된 법인 서류가 필요한 경우가 많습니다.",
    chain: [
      "권리 유형과 출원 구분(니스 분류 등)을 확정합니다.",
      "선행 상표·권리 조사를 진행합니다.",
      "위임장과 출원인 법인 서류를 준비하고 필요한 인증을 받습니다.",
      "DIP에 출원하고 심사 통지에 대응합니다.",
      "등록 후 갱신·사용 증거 관리 계획을 세웁니다.",
    ],
    checklist: [
      "상표 이미지 파일 또는 권리 대상물.",
      "출원인 법인등기부·회사증명서(해외 법인은 인증 번역 포함).",
      "위임장(양식은 DIP 기준).",
      "지정 상품·서비스 목록.",
      "선사용 증거(있는 경우).",
    ],
    pitfalls: [
      "선행 조사 없이 출원해 거절되는 경우.",
      "지정 상품 분류가 실제 사업과 어긋나는 경우.",
      "해외 법인 서류의 인증이 누락된 경우.",
      "갱신 기한 관리를 하지 않아 권리가 소멸하는 경우.",
    ],
    remote: "조사·출원·대응은 대부분 원격으로 가능합니다. 원본 위임장 우편 송부가 필요할 수 있습니다.",
  },
  litigation: {
    label: "소송 지원·법원 서류 서비스",
    definition:
      "소송 지원은 증거 서류의 번역·인증, 외국 서류의 증거능력 확보, 송달과 기일 관리 등 절차상 요건을 갖추는 일입니다. 증거 형식이 어긋나면 내용과 무관하게 배척될 수 있습니다.",
    chain: [
      "사건 유형과 관할 법원, 제출 기한을 확인합니다.",
      "증거 목록을 만들고 번역이 필요한 서류를 구분합니다.",
      "외국 서류에 인증 번역과 필요한 인증을 붙입니다.",
      "소장·답변서·증거설명서를 작성해 제출합니다.",
      "기일 진행과 추가 제출을 관리합니다.",
    ],
    checklist: [
      "계약서·영수증·통신 기록 등 원본 증거.",
      "상대방과 자신의 신분·법인 서류.",
      "외국 판결·공문서와 그 인증 번역.",
      "위임장과 인지·송달료 관련 정보.",
    ],
    pitfalls: [
      "번역 없는 외국어 증거를 그대로 제출하는 경우.",
      "제척기간·소멸시효를 확인하지 않고 준비하는 경우.",
      "증거 원본 보관 위치를 확인하지 않은 경우.",
      "외국 판결이 태국에서 자동 집행된다고 오해하는 경우.",
    ],
    remote: "서류 준비와 번역·인증은 원격으로 진행합니다. 기일은 변호사가 출석합니다.",
  },
  pdpa: {
    label: "PDPA·개인정보보호 서류 서비스",
    definition:
      "태국 개인정보보호법(PDPA)은 개인정보 처리에 관한 고지, 동의, 처리기록, 위탁계약, 국외이전 근거 등을 요구합니다. 실무는 문서화가 핵심입니다.",
    chain: [
      "개인정보 처리 흐름과 수집 항목을 매핑합니다.",
      "개인정보 처리방침과 동의 문구를 작성·정비합니다.",
      "처리 기록(ROPA)과 위탁·수탁 계약서를 정비합니다.",
      "국외이전 근거와 보안 조치를 문서화합니다.",
      "담당자 지정과 사고 대응 절차를 마련합니다.",
    ],
    checklist: [
      "수집·이용 항목과 목적 목록.",
      "위탁 업체·시스템 목록.",
      "현행 개인정보 처리방침과 동의 양식.",
      "해외 본사·클라우드 등 국외이전 현황.",
    ],
    pitfalls: [
      "타사 방침을 그대로 복사해 실제 처리와 불일치하는 경우.",
      "동의를 근거로 삼을 수 없는 처리에 동의만 받는 경우.",
      "위탁 계약에 PDPA 조항이 빠진 경우.",
      "태국어 고지문이 없는 경우.",
    ],
    remote: "전 과정 원격 진행이 가능합니다. 필요한 경우 현장 인터뷰를 병행합니다.",
  },
  "misc-official": {
    label: "기타 관공서 서류 서비스",
    definition:
      "위 카테고리에 들어가지 않는 관공서 서류 — 각종 증명서 발급, 확인서, 신고, 등본 재발급 등 — 도 동일한 원칙을 따릅니다. 어느 기관이 원본을 보관하는지 먼저 확인합니다.",
    chain: [
      "필요한 서류의 정확한 명칭과 발급 기관을 확인합니다.",
      "신청 자격과 필요한 증빙을 정리합니다.",
      "신청서를 작성하고 접수합니다.",
      "해외 사용 시 인증 번역을 작성합니다.",
      MFA,
    ],
    checklist: [
      "신분 서류(여권 또는 태국 신분증).",
      "요청 서류의 용도와 제출처 안내문.",
      "기존에 발급받은 관련 서류.",
      "대리 신청 시 위임장.",
    ],
    pitfalls: [
      "서류 명칭이 달라 엉뚱한 증명서를 발급받는 경우.",
      "제출처가 요구하는 발급 후 유효기간을 넘기는 경우.",
      "번역을 먼저 하고 나서 원본을 다시 받는 경우.",
      "기관별 접수 시간·휴무를 확인하지 않은 경우.",
    ],
    remote: "확인·신청·추적은 대부분 원격 대행이 가능합니다.",
  },
  council: {
    label: "태국 변호사협회 인증 서비스",
    definition:
      "일부 제출처는 서명 변호사가 태국 변호사협회(Lawyers Council of Thailand) 등록 공증업무 변호사인지 확인하는 협회 확인 절차를 추가로 요구합니다.",
    chain: [
      "제출처가 협회 확인까지 요구하는지 확인합니다.",
      "공증업무 변호사가 서류를 인증합니다.",
      "변호사협회에 확인 신청을 접수합니다.",
      "확인이 완료된 서류를 수령합니다.",
      MFA,
    ],
    checklist: [
      "인증 대상 원본 서류.",
      "제출처의 서면 요건(협회 확인 요구 문구).",
      "서명인의 신분 서류.",
      "해외 사용 시 최종 목적국 정보.",
    ],
    pitfalls: [
      "협회 확인이 필요 없는데 추가해 일정만 늘리는 경우.",
      "반대로 요구되는 확인을 빠뜨려 반송되는 경우.",
      "확인 후 서류를 수정해 다시 처음부터 진행해야 하는 경우.",
      "협회 접수 일정이 일정표에 반영되지 않은 경우.",
    ],
    remote: "확인 신청과 수령은 저희가 대행합니다. 서명은 출석이 필요합니다.",
  },
  "due-diligence": {
    label: "실사·검증 서비스",
    definition:
      "거래 전 실사는 공개 등기와 공적 기록으로 상대방의 실체, 권한, 권리관계를 확인하는 작업입니다. 확인된 사실만 보고서에 담고, 추정은 구분해 표시합니다.",
    chain: [
      "확인해야 할 대상과 질문 목록을 확정합니다.",
      "DBD 등기, 토지 등기, 법원·관보 등 공적 기록을 조회합니다.",
      "제출받은 서류의 진위와 최신성을 대조합니다.",
      "발견 사항과 위험 요소를 보고서로 정리합니다.",
      "필요 시 번역·인증본을 함께 제공합니다.",
    ],
    checklist: [
      "상대방 회사명·등록번호 또는 개인 신분 정보.",
      "검토 대상 계약서·권리증 사본.",
      "거래 구조와 확인이 필요한 쟁점.",
      "기한과 보고서 사용 목적.",
    ],
    pitfalls: [
      "등기 사본의 발급일이 오래된 경우.",
      "토지 권리증의 지목·부담 사항을 확인하지 않는 경우.",
      "서명자의 대표 권한을 확인하지 않는 경우.",
      "구두 설명을 근거로 계약을 진행하는 경우.",
    ],
    remote: "조회와 보고는 전부 원격으로 제공합니다. 현장 확인은 별도 협의합니다.",
  },
  pension: {
    label: "연금·해외 수급 서류 서비스",
    definition:
      "해외 연금·수급 기관은 생존확인서, 거주 증명, 혼인·가족 관계 서류를 정기적으로 요구합니다. 각 기관의 양식과 인증 형식을 그대로 따르는 것이 중요합니다.",
    chain: [
      "수급 기관의 현행 양식과 인증 요건을 확인합니다.",
      "생존확인·거주 증명 등 필요한 서류를 준비합니다.",
      "공증업무 변호사 또는 지정 기관이 확인·인증합니다.",
      "필요 시 인증 번역과 외교부 인증을 진행합니다.",
      "기한 내 제출하고 사본을 보관합니다.",
    ],
    checklist: [
      "수급 기관이 보낸 양식 원본.",
      "여권과 태국 체류 증명(비자·거주 증명).",
      "혼인·가족 관계 서류(요구되는 경우).",
      "수급자 계좌 정보와 이전 제출 이력.",
    ],
    pitfalls: [
      "기관 지정 양식이 아닌 일반 확인서를 제출하는 경우.",
      "제출 기한을 넘겨 지급이 정지되는 경우.",
      "이름 표기가 연금 기록과 다른 경우.",
      "인증 주체(공증인/영사/의사 등) 요건을 잘못 이해하는 경우.",
    ],
    remote: "확인·번역·인증 조율은 원격으로 진행합니다. 생존확인은 대면 확인이 필요합니다.",
  },
  "work-permit": {
    label: "취업허가(워크퍼밋) 서류 서비스",
    definition:
      "태국 취업허가는 고용주 측 서류와 근로자 측 서류가 함께 갖춰져야 하며, 비자 카테고리와 정합해야 합니다. 학위·경력 증명 등 외국 서류는 인증 번역이 필요합니다.",
    chain: [
      "직무와 비자 카테고리에 맞는 요건을 확인합니다.",
      "고용주 서류(법인 등기, 재무제표, 직원 명부)를 준비합니다.",
      "근로자 서류(학위·경력 증명)에 인증 번역과 인증을 붙입니다.",
      "노동청에 신청하고 심사 보완에 대응합니다.",
      "발급 후 비자 연장·신고 일정과 연동해 관리합니다.",
    ],
    checklist: [
      "여권과 현재 비자(논-B 등).",
      "고용계약서와 직무기술서.",
      "학위증·경력증명서(인증 번역 포함).",
      "고용주 법인 서류와 재무제표.",
      "규격에 맞는 사진과 건강 관련 서류.",
    ],
    pitfalls: [
      "비자 카테고리와 취업허가 직무가 맞지 않는 경우.",
      "외국 학위 서류의 인증이 누락된 경우.",
      "허가 없이 업무를 먼저 시작하는 경우.",
      "직무·근무지 변경 시 신고를 놓치는 경우.",
    ],
    remote: "서류 준비와 검토는 원격으로 진행합니다. 접수·수령은 규정에 따라 출석이 필요할 수 있습니다.",
  },
  expert: {
    label: "전문·복합 사건 서류 서비스",
    definition:
      "여러 기관과 국가가 얽힌 복합 사건은 단일 절차가 아니라 순서 설계의 문제입니다. 저희는 전체 경로를 먼저 그리고, 되돌릴 수 없는 단계 이전에 검증합니다.",
    chain: [
      "목적과 최종 제출처, 기한을 명확히 정의합니다.",
      "기관별 요건을 정리해 의존 관계를 그립니다.",
      "되돌릴 수 없는 단계를 표시하고 사전 검증합니다.",
      "번역·인증·제출을 순서대로 실행합니다.",
      "결과를 확인하고 후속 절차를 안내합니다.",
    ],
    checklist: [
      "사건 개요와 관련 서류 일체.",
      "관련 국가와 제출 기관 목록.",
      "기한과 우선순위.",
      "이전에 진행한 절차와 그 결과.",
    ],
    pitfalls: [
      "각 절차를 따로 진행해 순서가 어긋나는 경우.",
      "번역 후 원본이 교체되어 전부 다시 하는 경우.",
      "여러 대리인이 서로 다른 표기를 사용하는 경우.",
      "가장 긴 리드타임 항목을 마지막에 시작하는 경우.",
    ],
    remote: "설계·조율·문서 작업은 원격으로 진행하고, 출석이 필요한 단계만 별도로 안내합니다.",
  },
};
